import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Loader2, GraduationCap, CreditCard, ExternalLink, MapPin, Calendar, Clock, CheckCircle, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Returns true when the program's day_schedule has at least one row where
// start_time/end_time differ from another row's — i.e. days don't share the same window.
const hasPerDayTimes = (p) => {
    const ds = p?.day_schedule || [];
    if (ds.length < 2) return false;
    const first = `${ds[0].start_time || ''}-${ds[0].end_time || ''}`;
    return ds.some(s => `${s.start_time || ''}-${s.end_time || ''}` !== first);
};

// Compact human string of the program's days+times.
// Same time across all days → "Sat, Sun 14:00–16:00"
// Different times → "Sat 14:00–16:00 · Sun 14:30–16:30"
const programDayTimeText = (p) => {
    const ds = (p?.day_schedule || []).filter(s => s && s.day);
    if (ds.length === 0) {
        return `${(p?.days || []).join(', ') || '—'} ${p?.start_time || ''}–${p?.end_time || ''}`.trim();
    }
    if (!hasPerDayTimes(p)) {
        const days = ds.map(s => s.day).join(', ');
        return `${days} ${ds[0].start_time}–${ds[0].end_time}`;
    }
    return ds.map(s => `${s.day} ${s.start_time}–${s.end_time}`).join(' · ');
};

const Renewal = () => {
    const { user } = useAuth();
    const [children, setChildren] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [fees, setFees] = useState(null);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [branchSelections, setBranchSelections] = useState({}); // { [studentId]: branchName }
    const [programSelections, setProgramSelections] = useState({}); // { [studentId]: programId }
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [confirmMissedOpen, setConfirmMissedOpen] = useState(false);
    const [missedNames, setMissedNames] = useState([]);

    useEffect(() => {
        fetchData();
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const isDebtPaid = urlParams.get('debt_paid') === '1';
        if (sessionId) pollPaymentStatus(sessionId, 0, isDebtPaid);
    }, [user]);

    const fetchData = async () => {
        try {
            const effectiveId = user?.linked_parent_id || user?.id;
            const [childrenRes, feesRes, programsRes] = await Promise.all([
                axios.get(`${API_URL}/api/students`, { withCredentials: true }),
                axios.get(`${API_URL}/api/parents/${effectiveId}/fees`, { withCredentials: true }),
                axios.get(`${API_URL}/api/programs?for_renewal=true`, { withCredentials: true }),
            ]);
            setChildren(childrenRes.data || []);
            setFees(feesRes.data);
            setPrograms(programsRes.data || []);
            // Pre-fill branch + program from existing student.program_id
            const initialPrograms = {};
            const initialBranches = {};
            (childrenRes.data || []).forEach(c => {
                const targetProgramId = c.next_year_program_id || c.program_id;
                if (targetProgramId) {
                    initialPrograms[c.id] = targetProgramId;
                    const p = (programsRes.data || []).find(pr => pr.id === targetProgramId);
                    if (p?.location) initialBranches[c.id] = p.location;
                }
            });
            setProgramSelections(initialPrograms);
            setBranchSelections(initialBranches);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const pollPaymentStatus = async (sessionId, attempts = 0, isDebtPaid = false) => {
        if (attempts >= 15) {
            toast.info('Payment is processing. Refresh in a moment to see your receipt.');
            fetchData();
            return;
        }
        try {
            const response = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`, { withCredentials: true });
            if (response.data.payment_status === 'paid') {
                toast.success(isDebtPaid
                    ? 'Previous debt cleared! You can now renew your children.'
                    : 'Renewal payment successful! Receipt sent to your email.');
                fetchData();
                window.history.replaceState({}, '', window.location.pathname);
            } else if (response.data.status === 'expired') {
                toast.error('Payment session expired');
            } else {
                setTimeout(() => pollPaymentStatus(sessionId, attempts + 1, isDebtPaid), 3000);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Error checking payment status:', error);
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1, isDebtPaid), 3000);
        }
    };

    const branches = useMemo(() => {
        const set = new Set();
        (programs || []).forEach(p => { if (p.location) set.add(p.location); });
        return Array.from(set).sort();
    }, [programs]);

    const programsByBranch = useMemo(() => {
        const map = {};
        (programs || []).forEach(p => {
            if (!p.location) return;
            if (!map[p.location]) map[p.location] = [];
            map[p.location].push(p);
        });
        // Sort programs by their `order` field (asc), then by name
        Object.keys(map).forEach(loc => {
            map[loc].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.name || '').localeCompare(b.name || ''));
        });
        return map;
    }, [programs]);

    // Returns student age (in years) at the start of the upcoming school year.
    const studentAge = (child) => {
        if (!child?.date_of_birth) return null;
        const dob = new Date(child.date_of_birth);
        if (isNaN(dob)) return null;
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) age--;
        return age;
    };

    // Filter programs for a student by gender + age range (set on each program).
    const programsForStudent = (child, list) => {
        const age = studentAge(child);
        const gender = (child?.gender || '').toLowerCase();
        return (list || []).filter(p => {
            const elig = (p.gender_eligibility || 'both').toLowerCase();
            if (elig !== 'both' && gender && elig !== gender) return false;
            if (age != null) {
                if (p.min_age != null && age < p.min_age) return false;
                if (p.max_age != null && age > p.max_age) return false;
            }
            return true;
        });
    };

    const setBranch = (studentId, branch) => {
        setBranchSelections(prev => ({ ...prev, [studentId]: branch }));
        // Clear program if it doesn't belong to the new branch
        setProgramSelections(prev => {
            const currentProgram = (programs || []).find(p => p.id === prev[studentId]);
            if (currentProgram && currentProgram.location !== branch) {
                const next = { ...prev };
                delete next[studentId];
                return next;
            }
            return prev;
        });
    };

    const toggleStudent = (studentId) => {
        setSelectedStudents(prev =>
            prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
        );
    };

    const handleRenew = async () => {
        if (selectedStudents.length === 0) {
            toast.error('Please select at least one student to renew');
            return;
        }
        const missingBranch = selectedStudents.filter(sid => !branchSelections[sid]);
        if (missingBranch.length > 0) {
            toast.error('Please choose a branch for each selected student');
            return;
        }
        const missingProgram = selectedStudents.filter(sid => !programSelections[sid]);
        if (missingProgram.length > 0) {
            toast.error('Please choose a program for each selected student');
            return;
        }
        // Warn the guardian about any renewable students they DIDN'T select
        const skipped = renewableChildren.filter(c => !selectedStudents.includes(c.id));
        if (skipped.length > 0) {
            setMissedNames(skipped.map(c => `${c.first_name} ${c.last_name}`));
            setConfirmMissedOpen(true);
            return;
        }
        proceedToCheckout();
    };

    const proceedToCheckout = async () => {
        setConfirmMissedOpen(false);
        setPaying(true);
        try {
            const selections = {};
            selectedStudents.forEach(sid => { selections[sid] = programSelections[sid]; });
            const response = await axios.post(`${API_URL}/api/payments/checkout/renewal`, {
                student_ids: selectedStudents,
                program_selections: selections,
            }, { withCredentials: true });
            window.location.href = response.data.url;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to initiate renewal payment');
            setPaying(false);
        }
    };

    // Active students only — waiting-list / unregistered are not eligible to renew
    const eligible = children.filter(c => c.status === 'active');
    const waitingChildren = children.filter(c => c.status !== 'active');
    const renewableChildren = eligible.filter(c => !c.renewed_for_next_year);
    const alreadyRenewed = eligible.filter(c => c.renewed_for_next_year);

    const renewalCost = selectedStudents.length * 100;
    const hasPreviousDebt = (fees?.previous_debt || 0) > 0.005;
    const previousDebtAmount = fees?.previous_debt || 0;
    const [payingDebt, setPayingDebt] = useState(false);

    const handlePayPreviousDebt = async () => {
        setPayingDebt(true);
        try {
            const res = await axios.post(`${API_URL}/api/parents/pay-previous-debt`, {}, { withCredentials: true });
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                toast.error('Failed to start checkout');
                setPayingDebt(false);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start checkout');
            setPayingDebt(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="renewal-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Renewal</h1>
                    <p className="text-[#4B5563]">Renew enrollment for the next term</p>
                </div>

                {hasPreviousDebt && (
                    <Card className="border-[#EF4444] bg-[#FEF2F2]" data-testid="previous-debt-banner">
                        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex items-center gap-3 flex-1">
                                <CreditCard className="h-5 w-5 text-[#EF4444]" />
                                <div>
                                    <p className="font-medium text-[#991B1B]">Previous-year debt must be paid first</p>
                                    <p className="text-sm text-[#B91C1C]">Your account carries a previous-year debt of <span className="font-bold tabular-nums">{'\u20AC'}{previousDebtAmount.toFixed(2)}</span>. Please clear it before renewing.</p>
                                </div>
                            </div>
                            <Button
                                className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                                onClick={handlePayPreviousDebt}
                                disabled={payingDebt}
                                data-testid="pay-previous-debt-btn"
                            >
                                {payingDebt ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                                Pay {'\u20AC'}{previousDebtAmount.toFixed(2)} now
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Already-renewed students (locked) */}
                {alreadyRenewed.length > 0 && (
                    <Card className="card-surface" data-testid="already-renewed-section">
                        <CardHeader>
                            <CardTitle className="font-['Outfit'] flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-[#10B981]" />
                                Already Renewed for Next Year
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {alreadyRenewed.map(child => {
                                    const p = programs.find(pr => pr.id === child.next_year_program_id) || programs.find(pr => pr.id === child.program_id);
                                    return (
                                        <div key={child.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0]" data-testid={`renewed-student-${child.id}`}>
                                            <div className="flex items-center gap-3">
                                                <div className="h-10 w-10 rounded-full bg-[#10B981] flex items-center justify-center text-white font-medium">
                                                    {child.first_name[0]}{child.last_name[0]}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-[#1F2937]">{child.first_name} {child.last_name}</p>
                                                    <p className="text-xs text-[#4B5563]">
                                                        {p ? `${p.location ? p.location + ' — ' : ''}${p.name}` : 'Program selected'}
                                                        {child.renewal_receipt_number ? ` · Receipt ${child.renewal_receipt_number}` : ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <Badge className="bg-[#10B981] text-white"><Lock className="h-3 w-3 mr-1" />Locked</Badge>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Waiting-list / unregistered students (cannot renew) */}
                {waitingChildren.length > 0 && (
                    <Card className="card-surface border-[#F59E0B]/40" data-testid="waiting-list-blocked-section">
                        <CardContent className="p-4">
                            <p className="font-medium text-[#1F2937] mb-2">Waiting for admin approval</p>
                            <p className="text-sm text-[#4B5563] mb-3">The following students cannot be renewed yet — please wait until an administrator activates their enrollment.</p>
                            <div className="space-y-2">
                                {waitingChildren.map(child => (
                                    <div key={child.id} className="flex items-center justify-between p-3 rounded-lg bg-[#FFFBEB] border border-[#FCD34D]/60">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center text-[#92400E] font-medium">
                                                {child.first_name[0]}{child.last_name[0]}
                                            </div>
                                            <div>
                                                <p className="font-medium text-[#1F2937]">{child.first_name} {child.last_name}</p>
                                                <p className="text-xs text-[#4B5563]">Reg: {child.reg_code}</p>
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="text-[#F59E0B] border-[#F59E0B]">{child.status === 'waiting_list' ? 'Waiting List' : 'Unregistered'}</Badge>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Renewable students */}
                <Card className="card-surface">
                    <CardHeader>
                        <CardTitle className="font-['Outfit']">Select Students to Renew</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {renewableChildren.length > 0 ? (
                            <div className="space-y-3">
                                {renewableChildren.map((child) => {
                                    const selected = selectedStudents.includes(child.id);
                                    const branch = branchSelections[child.id];
                                    const programsForBranch = branch ? (programsByBranch[branch] || []) : [];
                                    const chosenProgram = programs.find(p => p.id === programSelections[child.id]);
                                    return (
                                        <div
                                            key={child.id}
                                            className={`rounded-lg border-2 transition-colors ${
                                                selected ? 'border-[#064E3B] bg-[#064E3B]/5' : 'border-[#E5E7EB] hover:border-[#064E3B]/30'
                                            }`}
                                            data-testid={`renewal-student-${child.id}`}
                                        >
                                            <div
                                                className={`flex items-center justify-between p-4 ${hasPreviousDebt ? '' : 'cursor-pointer'}`}
                                                onClick={() => !hasPreviousDebt && toggleStudent(child.id)}
                                            >
                                                <div className="flex items-center space-x-4">
                                                    <Checkbox
                                                        checked={selected}
                                                        disabled={hasPreviousDebt}
                                                        onCheckedChange={() => toggleStudent(child.id)}
                                                    />
                                                    <div className="h-10 w-10 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-medium">
                                                        {child.first_name[0]}{child.last_name[0]}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-[#1F2937]">{child.first_name} {child.last_name}</p>
                                                        <p className="text-xs text-[#4B5563] tabular-nums">Reg: {child.reg_code}</p>
                                                    </div>
                                                </div>
                                                <Badge variant="outline" className="tabular-nums">{'\u20AC'}100.00</Badge>
                                            </div>

                                            {selected && (
                                                <div className="border-t border-[#E5E7EB] px-4 py-3 space-y-3 bg-white/50" onClick={(e) => e.stopPropagation()}>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="text-xs font-medium text-[#4B5563] mb-1 block">Branch</label>
                                                            <Select value={branch || ''} onValueChange={(v) => setBranch(child.id, v)}>
                                                                <SelectTrigger data-testid={`branch-select-${child.id}`}>
                                                                    <SelectValue placeholder="Choose a branch" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {branches.map(b => (
                                                                        <SelectItem key={b} value={b}>{b}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-medium text-[#4B5563] mb-1 block">Program for next year</label>
                                                            <Select
                                                                value={programSelections[child.id] || ''}
                                                                onValueChange={(v) => setProgramSelections(prev => ({ ...prev, [child.id]: v }))}
                                                                disabled={!branch}
                                                            >
                                                                <SelectTrigger data-testid={`program-select-${child.id}`}>
                                                                    <SelectValue placeholder={branch ? 'Choose a program' : 'Pick a branch first'} />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {programsForStudent(child, programsForBranch).map(p => {
                                                                        const hasNY = Boolean(p.next_year_schedule && p.next_year_schedule.length);
                                                                        return (
                                                                            <SelectItem key={p.id} value={p.id}>
                                                                                <span>{p.name} ({programDayTimeText(p)})</span>
                                                                                {hasNY && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#92400E] font-semibold">· next year</span>}
                                                                            </SelectItem>
                                                                        );
                                                                    })}
                                                                    {programsForStudent(child, programsForBranch).length === 0 && (
                                                                        <div className="px-2 py-1.5 text-xs text-[#6B7280]">No programs match this student's gender and age.</div>
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                    {chosenProgram && (
                                                        <>
                                                            {hasPerDayTimes(chosenProgram) ? (
                                                                <div className="space-y-1 pt-1">
                                                                    <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                                                                        <MapPin className="h-3 w-3" /><span className="font-medium">{chosenProgram.location || '—'}</span>
                                                                        <span className="text-[#D1D5DB]">·</span>
                                                                        <GraduationCap className="h-3 w-3" /><span className="font-medium">{chosenProgram.name}</span>
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#4B5563] pl-1">
                                                                        {(chosenProgram.day_schedule || []).map(s => (
                                                                            <div key={s.day} className="flex items-center gap-1">
                                                                                <Calendar className="h-3 w-3" /><span>{s.day}</span>
                                                                                <Clock className="h-3 w-3 ml-1" />
                                                                                <span className="tabular-nums">{s.start_time}–{s.end_time}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs text-[#4B5563] pt-1">
                                                                    <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /><span className="font-medium">{chosenProgram.location || '—'}</span></div>
                                                                    <div className="flex items-center gap-1"><GraduationCap className="h-3 w-3" /><span className="font-medium">{chosenProgram.name}</span></div>
                                                                    <div className="flex items-center gap-1"><Calendar className="h-3 w-3" /><span>{(chosenProgram.days || []).join(', ') || '—'}</span></div>
                                                                    <div className="flex items-center gap-1"><Clock className="h-3 w-3" /><span className="tabular-nums">{chosenProgram.start_time}–{chosenProgram.end_time}</span></div>
                                                                </div>
                                                            )}
                                                            {chosenProgram.next_year_note && (
                                                                <div className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded px-2 py-1.5 mt-1">
                                                                    <span className="font-medium">Next year:</span> {chosenProgram.next_year_note}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8">
                                <GraduationCap className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563]">
                                    {alreadyRenewed.length > 0
                                        ? 'All your active students are already renewed for next year.'
                                        : (waitingChildren.length > 0
                                            ? 'No active students yet — pending admin approval.'
                                            : 'No active students to renew')}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {renewableChildren.length > 0 && (
                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Selected: {selectedStudents.length} student(s)</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{'\u20AC'}{renewalCost.toFixed(2)}</p>
                                </div>
                                <Button
                                    onClick={handleRenew}
                                    disabled={paying || selectedStudents.length === 0 || hasPreviousDebt}
                                    className="bg-[#064E3B] hover:bg-[#047857] h-12 px-8 text-lg"
                                    data-testid="renew-now-btn"
                                >
                                    {paying ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <RefreshCw className="h-5 w-5 mr-2" />}
                                    Renew Now
                                </Button>
                            </div>
                            <p className="text-xs text-[#4B5563] mt-2 flex items-center">
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Secure payment via Stripe
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Missed-kids confirmation dialog */}
                <Dialog open={confirmMissedOpen} onOpenChange={setConfirmMissedOpen}>
                    <DialogContent className="max-w-md" data-testid="missed-students-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit'] text-[#92400E] flex items-center gap-2">
                                <RefreshCw className="h-5 w-5" /> Some students will not be enrolled
                            </DialogTitle>
                            <DialogDescription className="text-[#4B5563] pt-2">
                                You haven't selected the following student{missedNames.length > 1 ? 's' : ''} for renewal:
                            </DialogDescription>
                        </DialogHeader>
                        <ul className="bg-[#FFFBEB] border border-[#FCD34D]/60 rounded-lg p-3 space-y-1 text-sm text-[#1F2937] list-disc list-inside" data-testid="missed-students-list">
                            {missedNames.map(n => <li key={n}>{n}</li>)}
                        </ul>
                        <p className="text-sm text-[#4B5563]">
                            If you continue, {missedNames.length > 1 ? 'they' : 'this student'} <strong>will not be enrolled for next year</strong>. You can come back and renew at any time before the deadline.
                        </p>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmMissedOpen(false)} data-testid="missed-dialog-cancel">Go back</Button>
                            <Button className="bg-[#064E3B] hover:bg-[#047857]" onClick={proceedToCheckout} data-testid="missed-dialog-confirm">
                                OK — continue to payment
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Renewal;
